import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
  ChangelogEntry,
  SystemStatus,
  MaintenanceWindow,
  DEFAULT_BETA_STATUS,
} from './statusData';

export interface StatusData {
  changelog: ChangelogEntry[];
  changelogError: boolean;
  systemStatus: SystemStatus[];
  systemStatusError: boolean;
  maintenanceWindows: MaintenanceWindow[];
  loading: boolean;
  betaStatus: any;
  retry: () => Promise<void>;
}

// Fetches all four public status RPCs once and every 4 hours. Previously
// received rows are retained on any fetch error — a failed refresh must not
// blank data the page already holds.
export function useStatusData(): StatusData {
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([]);
  const [changelogError, setChangelogError] = useState(false);
  const [systemStatus, setSystemStatus] = useState<SystemStatus[]>([]);
  const [systemStatusError, setSystemStatusError] = useState(false);
  const [maintenanceWindows, setMaintenanceWindows] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [betaStatus, setBetaStatus] = useState<any>(null);

  const fetchStatusData = useCallback(async () => {
    setLoading(true);

    const changelogPromise = supabase.rpc('get_changelog').then(res => {
      if (res.error) {
        console.error('Changelog RPC error:', res.error);
        setChangelogError(true);
      } else if (res.data) {
        setChangelog(res.data as ChangelogEntry[]);
        setChangelogError(false);
      }
    });

    const statusPromise = supabase.rpc('get_system_status').then(res => {
      if (res.error) {
        console.error('Status RPC error:', res.error);
        setSystemStatusError(true);
        return;
      }
      setSystemStatusError(false);
      if (res.data) setSystemStatus(res.data);
    }, () => {
      setSystemStatusError(true);
    });

    const maintenancePromise = supabase.rpc('get_maintenance_windows').then(res => {
      if (res.error) console.error('Maintenance RPC error:', res.error);
      if (res.data) setMaintenanceWindows(res.data);
    });

    const betaPromise = supabase.rpc('get_site_content', { p_key: 'beta_status' }).then(res => {
      if (!res.error && res.data) {
        setBetaStatus({ ...DEFAULT_BETA_STATUS, ...res.data });
      } else {
        // RPC returned an error or empty data.
        // Use the static fallback so the card always renders complete content.
        if (res.error) console.error('Beta status RPC error:', res.error);
        setBetaStatus(DEFAULT_BETA_STATUS);
      }
    }, () => {
      setBetaStatus(DEFAULT_BETA_STATUS);
    });

    try {
      await Promise.all([changelogPromise, statusPromise, maintenancePromise, betaPromise]);
    } catch (error) {
      console.error('Error fetching status data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatusData();

    // Auto-refresh every 4 hours
    const interval = setInterval(() => {
      fetchStatusData();
    }, 14400000); // 4 hours

    return () => clearInterval(interval);
  }, [fetchStatusData]);

  return {
    changelog,
    changelogError,
    systemStatus,
    systemStatusError,
    maintenanceWindows,
    loading,
    betaStatus,
    retry: fetchStatusData,
  };
}
